import {
  getTalkMoveTypeFlowerThresholds,
  getUtteranceLeafThreshold,
  isActiveTMGroup
} from '../utils/treeUtils';
import {
  BASE_FLOWERS,
  BASE_LEAVES,
  MAX_BRANCH_COUNT,
  FLOWERS_PER_MAIN_BRANCH,
  LEAVES_PER_MAIN_BRANCH,
  BASE_MAIN_BRANCHES
} from '../constants/constants';

function calculateMaxTreeCapacity() {
  if (MAX_BRANCH_COUNT <= BASE_MAIN_BRANCHES) {
    return BASE_FLOWERS;
  }
  
  const additionalBranches = MAX_BRANCH_COUNT - BASE_MAIN_BRANCHES;
  const maxFlowers = BASE_FLOWERS + (additionalBranches * FLOWERS_PER_MAIN_BRANCH);
  
  return maxFlowers;
}

function calculateMaxTreeLeaves() {
  if (MAX_BRANCH_COUNT <= BASE_MAIN_BRANCHES) {
    return BASE_LEAVES;
  }
  
  const additionalBranches = MAX_BRANCH_COUNT - BASE_MAIN_BRANCHES;
  const maxLeaves = BASE_LEAVES + (additionalBranches * LEAVES_PER_MAIN_BRANCH);
  
  return maxLeaves;
}

// Helper function to calculate session duration in seconds
function getSessionDuration(sessionMetadata) {
  if (!sessionMetadata) return 0;
  
  // Try duration field first
  if (sessionMetadata.duration) {
    return sessionMetadata.duration;
  }
  
  // Calculate from start/end times
  if (sessionMetadata.startTime && sessionMetadata.endTime) {
    // Convert milliseconds to seconds
    const durationMs = sessionMetadata.endTime - sessionMetadata.startTime;
    return Math.floor(durationMs / 1000);
  }
  
  // If only endTime exists, it might already be duration
  if (sessionMetadata.endTime && !sessionMetadata.startTime) {
    // Check if it's a reasonable duration (less than 24 hours in ms)
    if (sessionMetadata.endTime < 86400000) {
      return Math.floor(sessionMetadata.endTime / 1000);
    }
  }
  
  return 0;
}

export function processTreeDataForCounts(processedData, speakerType = 'student', sessionMetadata = null) {
  if (!processedData || !Array.isArray(processedData)) {
    return {
      totalFlowers: 0,
      totalLeaves: 0,
      branchCount: 5,
      processedDataCounts: {},
      flowerAllocation: {},
      chronologicalFlowers: [],
      treeFullnessInfo: null
    };
  }

  // Filter statements by speaker type
  const filteredStatements = processedData.filter(item =>
    item.speakerType.toLowerCase() === speakerType.toLowerCase()
  );

  // Get thresholds based on speaker type
  const thresholds = getTalkMoveTypeFlowerThresholds(speakerType);

  const processedDataCounts = Object.keys(thresholds).reduce((acc, key) => ({
    ...acc,
    [key]: 0
  }), {});

  const flowerAllocation = {};
  let currentFlowerIndex = 1;
  let currentLeafIndex = 1;
  let utteranceCount = 0;
  const flowers = [];
  const leaves = [];
  
  // Track when tree reaches full capacity
  let treeFullnessInfo = null;
  const maxFlowerCapacity = calculateMaxTreeCapacity();
  const maxLeafCapacity = calculateMaxTreeLeaves();
  // Get total session duration in seconds
  const totalSessionDuration = getSessionDuration(sessionMetadata);

  // Count categories/types
  for (let i = 0; i < filteredStatements.length; i++) {
    const statement = filteredStatements[i];
    const tmGroup = statement.talkMoveType;

    if (isActiveTMGroup(tmGroup, 'type')) {
      processedDataCounts[tmGroup] += 1;

      if (processedDataCounts[tmGroup] % thresholds[tmGroup] === 0) {
          const flowerId = `f${currentFlowerIndex++}`;
          const estimatedTime = totalSessionDuration > 0
            ? Math.floor((i / filteredStatements.length) * totalSessionDuration)
            : 0;

          flowers.push({
            id: flowerId,
            type: tmGroup,
            timestamp: estimatedTime,
            statementIndex: i
          });

          if (!treeFullnessInfo && flowers.length === maxFlowerCapacity) {
            treeFullnessInfo = {
              elapsedTime: estimatedTime,
              totalSessionTime: totalSessionDuration,
              maxFlowers: maxFlowerCapacity,
              achievedAt: 'flowers'
            };
          }
        }
    }

    // Handle utterance counts and leaves
    utteranceCount++;
    const leafThreshold = getUtteranceLeafThreshold(speakerType);
    if (utteranceCount % leafThreshold === 0) {
      const estimatedTime = totalSessionDuration > 0
        ? Math.floor((i / filteredStatements.length) * totalSessionDuration)
        : 0;

      leaves.push({
        index: currentLeafIndex++,
        timestamp: estimatedTime,
        statementIndex: i
      });

      // Check if tree just reached full capacity by leaves (only if not already full by flowers)
      if (!treeFullnessInfo && leaves.length === maxLeafCapacity) {
        treeFullnessInfo = {
          elapsedTime: estimatedTime,
          totalSessionTime: totalSessionDuration,
          maxLeaves: maxLeafCapacity,
          achievedAt: 'leaves'
        };
      }
    }
  }

  // Build flower allocation
  Object.keys(thresholds).forEach(tmGroup => {
    flowerAllocation[tmGroup] = {
      flowers: flowers.filter(f => f.type === tmGroup).map(f => f.id),
      count: processedDataCounts[tmGroup],
      threshold: thresholds[tmGroup],
      expectedFlowers: Math.floor(processedDataCounts[tmGroup] / thresholds[tmGroup])
    };
  });


  return {
    totalFlowers: flowers.length,
    totalLeaves: leaves.length,
    branchCount: 5, 
    processedDataCounts,
    flowerAllocation,
    chronologicalFlowers: flowers,
    treeFullnessInfo
  };
}
